import type { ComponentType } from 'react';
import type { InputSpec, Submission } from '@guess/engine';
import { ChoiceAnswer } from './ChoiceAnswer';
import { ColorAnswer } from './ColorAnswer';
import { NumberAnswer } from './NumberAnswer';
import { OrderAnswer } from './OrderAnswer';
import { TextAnswer } from './TextAnswer';

/**
 * REGISTRE 2 sur 2 : ce que le joueur MANIPULE.
 *
 * Symetrique du registre d'indices, et pour la meme raison. Le serveur decrit
 * l'entree par un `InputSpec` ; le client tient un widget par `kind`, pas un
 * par mini-jeu.
 *
 * Le `kind` de la `Submission` renvoyee DOIT correspondre a celui du spec —
 * c'est le contrat que le juge attend cote serveur. Chaque widget etant
 * type par sa variante, ce contrat est tenu par le compilateur, pas par la
 * relecture.
 *
 * Note importante sur l'etat interne : c'est le PARENT qui remonte ces
 * composants a chaque manche (via une cle). Un widget peut donc garder son
 * etat entre deux paliers — ce qui compte pour le QCM, dont les options se
 * reduisent au fil du temps et qui doit se souvenir de celles qu'il a vues.
 */

export interface AnswerProps<S extends InputSpec> {
  spec: S;
  /** Vrai des qu'on a repondu, ou que la manche est finie. */
  disabled: boolean;
  onSubmit(submission: Submission): void;
}

type AnswerRegistry = {
  [K in InputSpec['kind']]: ComponentType<AnswerProps<Extract<InputSpec, { kind: K }>>>;
};

const REGISTRY: AnswerRegistry = {
  text: TextAnswer,
  choice: ChoiceAnswer,
  order: OrderAnswer,
  color: ColorAnswer,
  number: NumberAnswer,
};

export function AnswerRenderer({ spec, disabled, onSubmit }: AnswerProps<InputSpec>) {
  // Meme assertion confinee que pour les indices, et sure pour la meme raison :
  // la table est complete, et elle est indexee par le discriminant lui-meme.
  const Component = REGISTRY[spec.kind] as ComponentType<AnswerProps<InputSpec>>;
  return <Component spec={spec} disabled={disabled} onSubmit={onSubmit} />;
}

/** Les formes d'entree connues. Sert au test de completude du registre. */
export const INPUT_KINDS = Object.keys(REGISTRY) as Array<InputSpec['kind']>;
